import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation, type TFunction } from 'next-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { Namespaces, LOCALE_SEARCH_PATHS as P } from 'types/locales';
import InlineSaveInput from 'components/atomic/molecules/inline-save-input';
import SavedSearchCard, { formatSearchSummary, AllSavedSearchesButton } from 'ui/saved-search';
import { useSavedSearchStore } from 'hooks/use-saved-search-store';
import { usePreferencesContext } from 'contexts/preferences';
import { AlertFrequency, summarizeFilter } from 'domain/saved-search';
import { GTM_CLICK_FILTER_SAVE_SEARCH } from 'constants/events';
import type { Filter } from 'contexts/preferences/listing-params/types';
import type { MapBounds } from 'domain/saved-search';
import { newListingsBadgeLabel } from './new-listings-badge-label';
import type { SelectedSavedSearch } from './saved-search-panel';
import { trackAreaPageV2Event } from '../track-area-page-v2-event';


/**
 * The message for a failed save, edit or delete, or undefined when there is
 * nothing to show.
 *
 * `useSavedSearchStore` reports either a list of DraftValidationError codes from
 * the domain policy or whatever the repository threw. Only the codes get their
 * own wording, since those are the cases a user can act on.
 */
function savedSearchErrorMessage(error: unknown, t: TFunction): string | undefined {
  if (!error) return undefined;

  if (Array.isArray(error) && error.includes('UNSUPPORTED_STATUS')) {
    return t(P.SAVED_SEARCH_PANEL_ERROR_UNSUPPORTED_STATUS, {
      defaultValue: 'Error: Saved Search currently only supports active listings',
    });
  }

  // A search with no location: on the search page the map is what locates a
  // search, so one saved before the map has reported its region has nothing
  // recording where it applies, and client-app rejects it. Actionable, so it
  // gets its own wording, matching the legacy save modal's.
  if (Array.isArray(error) && error.includes('MISSING_GEOGRAPHY')) {
    return t(P.SAVED_SEARCH_PANEL_ERROR_MISSING_LOCATION, {
      defaultValue: 'This search has no location yet. Move the map, then save again.',
    });
  }

  // MISSING_AREA_NAME is the only other code. It is unreachable from here: it
  // is asked only of a search located by an area slug, and a page showing one
  // always sets the area's name.
  return t(P.SAVED_SEARCH_PANEL_ERROR_GENERIC, {
    defaultValue: 'Something went wrong. Please try again.',
  });
}

/** Delay before showing loading skeletons, so quick reloads don't flash them. */
const SKELETON_DELAY_MS = 300;

/** Account page listing every saved search. */
const ALL_SAVED_SEARCHES_HREF = '/account/saved/searches';
/** Thumbnails for the "All Saved Searches" button, in fan order (left, right, center). */
const FAN_IMAGES = [
  { src: '/assets/images/area-page/saved-searches-1.webp' },
  { src: '/assets/images/area-page/saved-searches-2.webp' },
  { src: '/assets/images/area-page/saved-searches-3.webp' },
];

export type SavedSearchPanelContentProps = {
  onClose: () => void;
  /** The search params currently applied on the page — persisted when the user saves. */
  currentFilter?: Filter;
  /**
   * The map's current rectangle. Recorded on the saved search when the filter
   * has no area slug, which is the only thing locating a search made on the
   * search page. Pages that always have a slug can leave it out.
   */
  mapBounds?: MapBounds | null;
  /**
   * Called when the user picks "All Saved Searches". The page opens its
   * Collections view on the Saved Searches tab. Without this, the button goes
   * to the account page that lists them instead.
   */
  onViewAllSavedSearches?: () => void;
  /**
   * True on a page where the map is the search, so the saved search records the
   * map's rectangle and drops any slug the filter is carrying.
   */
  locatedByMap?: boolean;
  /**
   * Whether the panel is currently visible. Refetches recents whenever this
   * transitions to `true`. Defaults to `true` for callers (like the mobile sheet)
   * that fully unmount this component when closed, where a plain mount-effect
   * would otherwise suffice — but MUI `Popover`s can keep children mounted
   * through their exit transition, so a caller that toggles visibility via a
   * prop (not by mounting/unmounting) must pass its own `open` state here.
   */
  open?: boolean;
  /** Id of the currently-selected saved search (drives the card highlight). */
  selectedId?: string;
  /** Called when a card is selected. `applyToPage: false` selects without re-applying/panning. */
  onSelectSearch?: (search: SelectedSavedSearch, options?: { applyToPage?: boolean }) => void;
  /** Called when a card is unselected. */
  onUnselectSearch?: () => void;
};

/**
 * Save Current Search / Latest Searches content, shared between the desktop
 * `SavedSearchPanel` (MUI `Popover`) and the mobile swipe-up sheet
 * (`MobileSavedSearchSheet`). Exercises the `useSavedSearchStore` bridge: save the
 * current search, list recents, select/unselect, edit alert frequency, and delete.
 */
export function SavedSearchPanelContent({
  onClose,
  currentFilter,
  mapBounds,
  locatedByMap,
  onViewAllSavedSearches,
  open = true,
  selectedId,
  onSelectSearch,
  onUnselectSearch,
}: SavedSearchPanelContentProps) {
  const router = useRouter();
  const { t } = useTranslation(Namespaces.SEARCH);
  const { listingParams } = usePreferencesContext();
  const {
    recents,
    isLoading,
    error,
    fetchRecents,
    saveCurrentSearch,
    updateFrequency,
    editSavedSearch,
    removeSavedSearch,
  } = useSavedSearchStore();

  // A reload covers the whole save/delete window (the operation + the reload of the
  // latest 3) so the recents list never sits stale/empty while it reloads.
  const [isBusy, setIsBusy] = useState(false);
  const isReloading = isBusy || isLoading;

  const runBusy = (operation: () => Promise<unknown>) => {
    setIsBusy(true);
    void operation().finally(() => setIsBusy(false));
  };

  // Only show the skeletons once a reload has lasted past a short threshold, so quick
  // reloads don't flash them. The cleanup resets the flag for the next reload.
  const [skeletonDelayElapsed, setSkeletonDelayElapsed] = useState(false);
  useEffect(() => {
    if (!isReloading) return undefined;
    const timeoutId = window.setTimeout(() => setSkeletonDelayElapsed(true), SKELETON_DELAY_MS);
    return () => {
      window.clearTimeout(timeoutId);
      setSkeletonDelayElapsed(false);
    };
  }, [isReloading]);
  const showSkeletons = isReloading && skeletonDelayElapsed;

  const handleViewAllSavedSearches = () => {
    onClose();
    if (onViewAllSavedSearches) {
      onViewAllSavedSearches();
      return;
    }
    void router.push(ALL_SAVED_SEARCHES_HREF);
  };

  useEffect(() => {
    if (open) void fetchRecents();
  }, [open, fetchRecents]);

  const areaLabel = currentFilter?.areaName?.trim();
  const saveLabel = areaLabel
    ? t(P.SAVED_SEARCH_PANEL_SAVE_NAMED, { area: areaLabel, defaultValue: `Save "${areaLabel}"` })
    : t(P.SAVED_SEARCH_PANEL_SAVE_CURRENT, { defaultValue: 'Save Current Search' });

  // Suggested name (area + filters), matching the legacy area page's default. Cloned
  // so the generator's in-place homeType tweak can't mutate the live filter.
  const suggestedName = currentFilter
    ? formatSearchSummary(summarizeFilter(currentFilter), t)
    : '';

  // Reuse the existing notification-frequency labels rather than duplicating them.
  const frequencyOptions = [
    { value: '0', label: t(P.SAVED_SEARCH_NOTIFICATION_FREQUENCY_NO_ALERTS, { defaultValue: 'No alerts' }) },
    { value: '-1', label: t(P.SAVED_SEARCH_NOTIFICATION_FREQUENCY_INSTANT, { defaultValue: 'Instant' }) },
    { value: '86400', label: t(P.SAVED_SEARCH_NOTIFICATION_FREQUENCY_DAILY, { defaultValue: 'Daily' }) },
    { value: '604800', label: t(P.SAVED_SEARCH_NOTIFICATION_FREQUENCY_WEEKLY, { defaultValue: 'Weekly' }) },
  ];

  const errorMessage = savedSearchErrorMessage(error, t);

  const cardLabels = {
    options: t(P.SAVED_SEARCH_PANEL_OPTIONS, { defaultValue: 'Saved search options' }),
    rename: t(P.SAVED_SEARCH_PANEL_RENAME, { defaultValue: 'Rename' }),
    delete: t(P.SAVED_SEARCH_PANEL_DELETE, { defaultValue: 'Delete' }),
    frequency: t(P.SAVED_SEARCH_PANEL_FREQUENCY_LABEL, { defaultValue: 'Alert frequency' }),
    confirmRename: t(P.SAVED_SEARCH_PANEL_CONFIRM_RENAME, { defaultValue: 'Finish rename' }),
    unselect: t(P.SAVED_SEARCH_PANEL_UNSELECT, { defaultValue: 'Unselect' }),
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'min(640px, calc(100vh - 12rem))' }}>
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontFamily: 'var(--themes-fonts-heading), Georgia, serif', fontSize: '1.125rem', color: '#0F1729', mb: 1.5 }}>
          {t(P.SAVED_SEARCH_PANEL_SAVE_CURRENT, { defaultValue: 'Save Current Search' })}
        </Typography>
        <InlineSaveInput
          placeholder={saveLabel}
          defaultValue={suggestedName}
          saveLabel={t(P.SAVED_SEARCH_PANEL_SAVE, { defaultValue: 'Save' })}
          onSave={name => {
            const filterToSave = listingParams?.filter ?? currentFilter;
            if (!filterToSave) return;
            trackAreaPageV2Event(GTM_CLICK_FILTER_SAVE_SEARCH);
            // Auto-select the newly saved search so it becomes the active one. It was
            // saved from the current view, so select without re-applying/panning.
            runBusy(() =>
              saveCurrentSearch(filterToSave, { name, bounds: mapBounds, locatedByMap }).then(created => {
                if (created) {
                  onSelectSearch?.({
                    id: created.id,
                    title: created.description,
                    imageUrl: created.imageUrl,
                    filter: created.filter,
                  }, { applyToPage: false });
                }
              })
            );
          }}
        />
        {errorMessage && (
          <Typography role="alert" sx={{ mt: 1, fontSize: 13, lineHeight: 1.4, color: '#B3261E' }}>
            {errorMessage}
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        <Typography sx={{ fontFamily: 'var(--themes-fonts-heading), Georgia, serif', fontSize: '1.125rem', color: '#0F1729', mb: 1.5, lineHeight: '1.3' }}>
          {t(P.SAVED_SEARCH_PANEL_LATEST, { defaultValue: 'Latest Searches' })}
        </Typography>
        {!isReloading && recents.length === 0 && (
          <Typography sx={{ fontFamily: 'var(--themes-fonts-text), sans-serif', color: '#6B7280', fontSize: 14 }}>{t(P.SAVED_SEARCH_PANEL_EMPTY, { defaultValue: 'No saved searches yet.' })}</Typography>
        )}
        <Stack spacing={1.5}>
          {showSkeletons
            ? Array.from({ length: 3 }, (_, i) => <SavedSearchCard key={`skeleton-${i}`} loading />)
            : recents.map(record => (
              <SavedSearchCard
                key={record.id}
                title={record.description}
                summary={formatSearchSummary(record.summary, t)}
                labels={cardLabels}
                imageUrl={record.imageUrl}
                newListingsLabel={newListingsBadgeLabel(record.newListingsCount, t)}
                frequencyValue={String(record.frequency.seconds)}
                frequencyOptions={frequencyOptions}
                onFrequencyChange={value => {
                  void updateFrequency(record, AlertFrequency.from(Number(value)));
                }}
                selected={record.id === selectedId}
                onSelect={() => onSelectSearch?.({ id: record.id, title: record.description, imageUrl: record.imageUrl, filter: record.filter })}
                onUnselect={onUnselectSearch}
                onRename={name => {
                  void editSavedSearch(record, { name });
                  // Keep the top-bar bookmark's title in sync when the active one is renamed.
                  if (record.id === selectedId) {
                    onSelectSearch?.(
                      { id: record.id, title: name, imageUrl: record.imageUrl, filter: record.filter },
                      { applyToPage: false }
                    );
                  }
                }}
                onDelete={() => {
                  runBusy(() => removeSavedSearch(record));
                  // Clear the top-bar bookmark if the active search was the one deleted.
                  if (record.id === selectedId) onUnselectSearch?.();
                }}
              />
            ))}
        </Stack>

        <AllSavedSearchesButton
          images={FAN_IMAGES}
          title={t(P.SAVED_SEARCH_PANEL_ALL_TITLE, { defaultValue: 'All Saved Searches' })}
          subtitle={t(P.SAVED_SEARCH_PANEL_ALL_SUBTITLE, { defaultValue: 'View Every Saved Search You\'ve Saved' })}
          onClick={handleViewAllSavedSearches}
          sx={{ mt: 1.5 }}
        />
      </Box>
    </Box>
  );
}
